import { h, render } from 'vue'
import ActionConfirmDialog from '~/components/base/ActionConfirmDialog.vue'
import { lockBodyScroll, unlockBodyScroll } from '~/utils/modal'

/**
 * @description Command-style action confirmation dialog.
 * @param {Object} options
 * @param {string} options.title - Dialog title text.
 * @param {string} [options.content] - Dialog content text.
 * @param {boolean} [options.showCancel=true] - Whether to show cancel button.
 * @param {boolean} [options.showOk=true] - Whether to show ok button.
 * @param {string} [options.cancelText='取消'] - Cancel button text.
 * @param {string} [options.okText='确定'] - Ok button text.
 * @param {boolean} [options.maskClosable=true] - Whether clicking the mask closes without choosing an action.
 * @param {Function} [options.onCancel] - Called when cancelled.
 * @param {Function} [options.onOk] - Called when confirmed. Return false to keep dialog open.
 * @param {Function} [options.onClose] - Called once when dialog closes.
 * @returns {{close: Function}|null}
 */
export const confirmAction = (options = {}) => {
  if (!import.meta.client) return null

  const container = document.createElement('div')
  document.body.appendChild(container)
  lockBodyScroll()

  let isClosed = false
  let closeTimer = null

  const cleanup = () => {
    if (closeTimer) {
      clearTimeout(closeTimer)
      closeTimer = null
    }
    unlockBodyScroll()
    render(null, container)
    container.parentNode?.removeChild(container)
  }

  const close = (result = { ok: false, cancel: false }) => {
    if (isClosed) return
    isClosed = true
    options.onClose?.(result)
    renderDialog(false)
    closeTimer = setTimeout(cleanup, 200)
  }

  const renderDialog = (visible) => {
    const vnode = h(ActionConfirmDialog, {
      visible,
      title: options.title,
      content: options.content,
      showCancel: options.showCancel ?? true,
      showOk: options.showOk ?? true,
      cancelText: options.cancelText,
      okText: options.okText,
      maskClosable: options.maskClosable ?? true,
      'onUpdate:visible': (nextVisible) => {
        if (!nextVisible) close({ ok: false, cancel: false })
      },
      onCancel: () => {
        options.onCancel?.()
        close({ ok: false, cancel: true })
      },
      onOk: async () => {
        const result = await options.onOk?.()
        if (result !== false) close({ ok: true, cancel: false })
      },
    })

    const { vueApp } = useNuxtApp()
    vnode.appContext = vueApp._context
    render(vnode, container)
  }

  renderDialog(true)

  return { close }
}
